import { Controller } from "@hotwired/stimulus"
import { usesSidebarLayout } from "../utils/layout_media"
const URL_PARAM = "open"

// Desktop right sidebar for the Events list (mirrors msg-detail / Network).
// Rows with data-detail-url fetch enriched event context from the server.
// Mobile keeps the detail-modal overlay via data-detail-template-id.
export default class extends Controller {
    connect() {
        this._onClick = (e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
            let row = e.target.closest("[data-detail-url]")
            if (!row || !this.element.contains(row)) return
            if (!this._useSidebar()) return
            e.preventDefault()
            e.stopPropagation()
            this._open(row)
        }
        this._onResize = () => {
            if (this._sidebarEl && !this._useSidebar()) this._close()
        }
        this._onKeydown = (e) => {
            if (e.key === "Escape") this._close()
        }
        this._onBeforeCache = () => this._close()
        this._onFrameLoad = (e) => {
            if (e.target.id === "events_list") this._restoreFromUrl()
        }
        this.element.addEventListener("click", this._onClick, true)
        window.addEventListener("resize", this._onResize)
        document.addEventListener("turbo:before-cache", this._onBeforeCache)
        document.addEventListener("turbo:frame-load", this._onFrameLoad)
        this._restoreFromUrl()
    }

    disconnect() {
        this.element.removeEventListener("click", this._onClick, true)
        window.removeEventListener("resize", this._onResize)
        document.removeEventListener("turbo:before-cache", this._onBeforeCache)
        document.removeEventListener("turbo:frame-load", this._onFrameLoad)
        this._teardownSidebar()
    }

    _useSidebar() {
        return usesSidebarLayout()
    }

    _restoreFromUrl() {
        if (!this._useSidebar()) return
        let key = new URL(window.location.href).searchParams.get(URL_PARAM)
        if (!key) return
        let row = this.element.querySelector(`[data-detail-key="${CSS.escape(key)}"]`)
        if (!row) return
        this._open(row)
        row.scrollIntoView({ block: "nearest" })
    }

    _open(row) {
        if (this._sidebarEl && this._activeRow === row) {
            this._close()
            return
        }

        this._hideJaneSidebar()

        let phone = document.querySelector(".assistant-phone")
        if (!phone) return

        if (!this._sidebarEl) {
            let sidebar = document.createElement("aside")
            sidebar.className = "detail-sidebar detail-sidebar--event"
            sidebar.setAttribute("role", "complementary")
            sidebar.setAttribute("aria-label", "Event details")
            sidebar.innerHTML = `
                <div class="detail-sidebar-actions">
                    <button type="button" class="detail-sidebar-close assistant-header-btn assistant-filter-toggle assistant-avatar" aria-label="Close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
                <div class="detail-sidebar-content"></div>`
            sidebar.querySelector(".detail-sidebar-close").addEventListener("click", () => this._close())
            phone.appendChild(sidebar)
            phone.classList.add("is-detail-open")
            this._sidebarEl = sidebar
            document.addEventListener("keydown", this._onKeydown)
        }

        if (this._activeRow) this._activeRow.classList.remove("is-selected")
        this._activeRow = row
        row.classList.add("is-selected")
        this._setUrlParam(row.dataset.detailKey)
        this._load(row)
    }

    _load(row) {
        this._unmountActionsMenu()
        let content = this._sidebarEl.querySelector(".detail-sidebar-content")
        content.innerHTML = `
            <div class="msg-detail-loading">
                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                <span>Loading…</span>
            </div>`

        let token = (this._loadToken = (this._loadToken || 0) + 1)
        fetch(row.dataset.detailUrl, {
            headers: { "Accept": "text/html" },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((html) => {
                if (token !== this._loadToken || !this._sidebarEl) return
                content.innerHTML = html
                this._mountActionsMenu(content)
            })
            .catch(() => {
                if (token !== this._loadToken || !this._sidebarEl) return
                if (this._loadTemplate(row, content)) return
                let fallback = row.dataset.eventPageUrl || row.dataset.detailUrl
                content.innerHTML = `
                    <div class="msg-detail-loading msg-detail-loading--error">
                        <span>Could not load this event.</span>
                        <a href="${fallback}" target="_blank" rel="noopener">Open in new window</a>
                    </div>`
            })
    }

    _mountActionsMenu(content) {
        let menu = content?.querySelector(".msg-detail-menu")
        if (!menu || !this._sidebarEl) return
        let actions = this._sidebarEl.querySelector(".detail-sidebar-actions")
        let closeBtn = actions?.querySelector(".detail-sidebar-close")
        if (!actions || !closeBtn) return
        this._unmountActionsMenu()
        actions.insertBefore(menu, closeBtn)
    }

    _unmountActionsMenu() {
        this._sidebarEl?.querySelectorAll(".detail-sidebar-actions .msg-detail-menu")
            .forEach((el) => el.remove())
    }

    _loadTemplate(row, content) {
        let templateId = row.dataset.detailTemplateId
        if (!templateId) return false
        let template = document.getElementById(templateId)
        if (!template?.content) return false
        content.replaceChildren(template.content.cloneNode(true))
        return true
    }

    _close() {
        this._setUrlParam(null)
        this._teardownSidebar()
        this._restoreJaneSidebar()
    }

    _hideJaneSidebar() {
        let jane = document.querySelector(".detail-sidebar--jane")
        if (!jane) return
        let ctrl = this.application.getControllerForElementAndIdentifier(jane, "jane-sidebar")
        if (ctrl) ctrl.hideForDetail()
        else jane.remove()
    }

    _restoreJaneSidebar() {
        document.dispatchEvent(new CustomEvent("jane-sidebar:restore"))
    }

    _teardownSidebar() {
        if (this._activeRow) {
            this._activeRow.classList.remove("is-selected")
            this._activeRow = null
        }
        // Only a panel this controller opened is ours to close: a frame visit's
        // turbo:before-cache lands here too, and the open Jane panel (whose
        // is-detail-open this is then) must survive it.
        if (this._sidebarEl) {
            this._sidebarEl.remove()
            this._sidebarEl = null
            document.removeEventListener("keydown", this._onKeydown)
            document.querySelector(".assistant-phone")?.classList.remove("is-detail-open")
        }
    }

    _setUrlParam(key) {
        let url = new URL(window.location.href)
        if (key) {
            url.searchParams.set(URL_PARAM, key)
        } else if (url.searchParams.has(URL_PARAM)) {
            url.searchParams.delete(URL_PARAM)
        } else {
            return
        }
        window.history.replaceState({}, "", url)
    }
}
